import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import {
  adminGetProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct,
} from "@/lib/adminApi";
import ImagePicker, { resolveImg } from "@/components/admin/ImagePicker";

const fmt = (n) => "€" + Number(n || 0).toFixed(2).replace(".", ",");
const CATEGORIES = ["Huiles", "Eaux florales", "Rituels corps", "Coffrets", "Visage", "Corps", "Cheveux"];

const EMPTY = {
  name: "", subtitle: "", category: "Huiles", badge: "", price: 0, sale_price: null,
  certif: "Bio · CE", short_desc: "", long_desc: "", story: "", benefits: [], ingredients: "",
  usage: "", image: "", gallery: [], color: "#1A3A2A", volume: "", stock: 0, active: true, order: 0,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product object or null
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    adminGetProducts().then(setProducts).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing({ ...EMPTY }); setIsNew(true); setErr(""); };
  const openEdit = (p) => { setEditing({ ...EMPTY, ...p }); setIsNew(false); setErr(""); };
  const close = () => setEditing(null);

  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      const payload = {
        ...editing,
        price: parseFloat(editing.price) || 0,
        sale_price: editing.sale_price === "" || editing.sale_price === null ? null : parseFloat(editing.sale_price),
        stock: parseInt(editing.stock) || 0,
        order: parseInt(editing.order) || 0,
        benefits: Array.isArray(editing.benefits) ? editing.benefits : String(editing.benefits || "").split("\n").filter(Boolean),
      };
      if (isNew) await adminCreateProduct(payload);
      else await adminUpdateProduct(editing.id, payload);
      close();
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
    setSaving(false);
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ?`)) return;
    await adminDeleteProduct(p.id);
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="admin-topbar" style={{ marginBottom: "1.5rem" }}>
        <h1 className="admin-title">Produits</h1>
        <button className="adm-btn" onClick={openNew} data-testid="product-new"><Plus size={15} /> Nouveau produit</button>
      </div>

      {loading ? (
        <div className="adm-loader"><div className="spinner" /></div>
      ) : (
        <table className="admin-table" data-testid="products-table">
          <thead>
            <tr><th></th><th>Produit</th><th>Catégorie</th><th>Prix</th><th>Stock</th><th>Statut</th><th></th></tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.id}`}>
                <td>{p.image ? <img className="admin-thumb" src={resolveImg(p.image)} alt="" /> : <div className="admin-thumb admin-thumb--empty" />}</td>
                <td><strong style={{ color: "var(--emerald)" }}>{p.name}</strong><br /><span className="adm-hint">{p.subtitle}</span></td>
                <td>{p.category}</td>
                <td>
                  {p.sale_price ? (
                    <><span style={{ textDecoration: "line-through", color: "var(--muted)" }}>{fmt(p.price)}</span> <strong style={{ color: "var(--terracotta)" }}>{fmt(p.sale_price)}</strong></>
                  ) : fmt(p.price)}
                </td>
                <td>{p.stock}</td>
                <td>{p.active === false ? <span className="pill gray">Masqué</span> : <span className="pill green">Actif</span>}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="icon-btn" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`}><Pencil size={15} /></button>
                  <button className="icon-btn danger" onClick={() => remove(p)} data-testid={`product-delete-${p.id}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing ? (
        <div className="adm-overlay" onClick={close}>
          <div className="adm-drawer" onClick={(e) => e.stopPropagation()} data-testid="product-drawer">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2>{isNew ? "Nouveau produit" : "Modifier le produit"}</h2>
              <button className="icon-btn" onClick={close}><X size={20} /></button>
            </div>
            {err ? <div className="adm-msg error">{err}</div> : null}
            <div className="adm-form">
              <div className="adm-field">
                <label>Nom</label>
                <input value={editing.name} onChange={(e) => setField("name", e.target.value)} data-testid="pf-name" />
              </div>
              <div className="adm-field">
                <label>Sous-titre</label>
                <input value={editing.subtitle} onChange={(e) => setField("subtitle", e.target.value)} data-testid="pf-subtitle" />
              </div>
              <div className="adm-row">
                <div className="adm-field">
                  <label>Catégorie</label>
                  <select value={editing.category} onChange={(e) => setField("category", e.target.value)} data-testid="pf-category">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="adm-field">
                  <label>Badge (Bestseller, Nouveau…)</label>
                  <input value={editing.badge} onChange={(e) => setField("badge", e.target.value)} data-testid="pf-badge" />
                </div>
              </div>
              <div className="adm-row-3">
                <div className="adm-field">
                  <label>Prix (€)</label>
                  <input type="number" step="0.01" value={editing.price} onChange={(e) => setField("price", e.target.value)} data-testid="pf-price" />
                </div>
                <div className="adm-field">
                  <label>Prix promo (€)</label>
                  <input type="number" step="0.01" placeholder="—" value={editing.sale_price ?? ""} onChange={(e) => setField("sale_price", e.target.value)} data-testid="pf-sale" />
                </div>
                <div className="adm-field">
                  <label>Stock</label>
                  <input type="number" value={editing.stock} onChange={(e) => setField("stock", e.target.value)} data-testid="pf-stock" />
                </div>
              </div>
              <div className="adm-row-3">
                <div className="adm-field">
                  <label>Volume</label>
                  <input value={editing.volume} onChange={(e) => setField("volume", e.target.value)} data-testid="pf-volume" />
                </div>
                <div className="adm-field">
                  <label>Certif.</label>
                  <input value={editing.certif} onChange={(e) => setField("certif", e.target.value)} />
                </div>
                <div className="adm-field">
                  <label>Ordre</label>
                  <input type="number" value={editing.order} onChange={(e) => setField("order", e.target.value)} />
                </div>
              </div>

              <ImagePicker label="Image principale" value={editing.image} onChange={(v) => setField("image", v)} testid="pf-image" />

              <div className="adm-field">
                <label>Galerie (images supplémentaires)</label>
                <div className="gallery-thumbs">
                  {(editing.gallery || []).map((g, i) => (
                    <div className="gt" key={i}>
                      <img src={resolveImg(g)} alt="" />
                      <button type="button" onClick={() => setField("gallery", editing.gallery.filter((_, j) => j !== i))}>×</button>
                    </div>
                  ))}
                </div>
                <ImagePicker label="" value="" onChange={(v) => v && setField("gallery", [...(editing.gallery || []), v])} testid="pf-gallery-add" />
              </div>

              <div className="adm-field">
                <label>Description courte</label>
                <textarea rows={2} value={editing.short_desc} onChange={(e) => setField("short_desc", e.target.value)} data-testid="pf-short" />
              </div>
              <div className="adm-field">
                <label>Description longue</label>
                <textarea rows={4} value={editing.long_desc} onChange={(e) => setField("long_desc", e.target.value)} />
              </div>
              <div className="adm-field">
                <label>Histoire / storytelling (bloc « L'histoire » sur la fiche)</label>
                <textarea rows={3} value={editing.story || ""} onChange={(e) => setField("story", e.target.value)} data-testid="pf-story" />
              </div>
              <div className="adm-field">
                <label>Bénéfices (un par ligne)</label>
                <textarea rows={3} value={Array.isArray(editing.benefits) ? editing.benefits.join("\n") : editing.benefits} onChange={(e) => setField("benefits", e.target.value.split("\n"))} />
              </div>
              <div className="adm-field">
                <label>Composition (INCI)</label>
                <textarea rows={2} value={editing.ingredients} onChange={(e) => setField("ingredients", e.target.value)} />
              </div>
              <div className="adm-field">
                <label>Conseils d'utilisation</label>
                <textarea rows={2} value={editing.usage} onChange={(e) => setField("usage", e.target.value)} />
              </div>
              <div className="adm-field" style={{ flexDirection: "row", alignItems: "center", gap: "0.6rem" }}>
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setField("active", e.target.checked)} id="pf-active" data-testid="pf-active" style={{ width: "auto" }} />
                <label htmlFor="pf-active" style={{ margin: 0 }}>Produit visible sur le site</label>
              </div>
            </div>
            <div className="adm-drawer-actions">
              <button className="adm-btn" onClick={save} disabled={saving} data-testid="product-save">{saving ? "Enregistrement…" : "Enregistrer"}</button>
              <button className="adm-btn ghost" onClick={close}>Annuler</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
